import { UserPermission, UserRole, retry } from '@color/lib';
import { compact } from 'lodash-es';
import { ReactNode, lazy } from 'react';

import { View as CNVCandidatesBrowseReviewView } from './CNVCandidates/BrowseReview/view';
import { BASE_CNV_CANDIDATES_PATH } from './CNVCandidates/paths';
import { CLINICAL_BASE_PATH } from './constants';
import { BASE_DATA_EXPORTS_PATH } from './DataExports/paths';
import { BASE_FULFILLMENT_PATH } from './Fulfillment/paths';
import { HealthHistoryPage } from './GenericHealthHistory/GenericHealthHistoryPage';
import { GENERIC_HEALTH_HISTORY_FIND_PATH } from './GenericHealthHistory/paths';
import { IGV_PATH } from './IGV/paths';
import { View as IGVView } from './IGV/view';
import { View as QCMetricsBatchListView } from './QCMetrics/BatchList/view';
import { QC_METRICS_BATCHES_PATH } from './QCMetrics/paths';
import { BASE_SAMPLE_SEARCH_PATH } from './SampleSearch/paths';
import { View as SampleSearchView } from './SampleSearch/view';
import { VariantClassificationListView } from './VariantClassification/ListView';
import { VARIANT_CLASSIFICATION_LIST_PATH } from './VariantClassification/paths';
import { ListView as VariantConfirmationListView } from './VariantConfirmation/ListView';
import { VARIANT_CONFIRMATION_LIST_PATH } from './VariantConfirmation/paths';

// Will use SamplesOnHold when React navbar is ready (UV-2385)
// import { SamplesOnHold } from 'ui/Clinical/SampleManagement/SamplesOnHold/SamplesOnHold';
// TODO(https://getcolor.atlassian.net/browse/UV-3396):

const ClinicalDataExports = lazy(() => retry(() => import('./DataExports')));

export interface AuthorizationItem {
  label: string;
  authorization?: {
    allowedRoles?: UserRole[];
    requiredPermissions?: UserPermission[];
  };
}
export interface IRouteDetail extends AuthorizationItem {
  pathname: string;
  element?: ReactNode;
}

export const MARKETING_LINK_GENERATOR_PATH = `${CLINICAL_BASE_PATH}/marketing-link-generator`;
export const MARKETING_LINK_VALIDATOR_PATH = `${CLINICAL_BASE_PATH}/marketing-link-validator`;

export const USER_MENU_ROUTES: IRouteDetail[] = [
  {
    pathname: '/admin',
    label: 'Admin',
  },
  {
    pathname: '/citations',
    label: 'Citation List',
  },
  {
    pathname: '/product-simulation',
    label: 'Product Simulation',
  },
  {
    pathname: '/pgx/genes',
    label: 'PGx Genes',
  },
  {
    pathname: '/pgx/explore',
    label: 'PGx Explorer',
  },
].map((routeDetails) => ({
  ...routeDetails,
  authorization: {
    allowedRoles: [UserRole.COLOR_EMPLOYEE],
  },
}));

export const CLINICAL_PARTNERS_ROUTES: IRouteDetail[] = [
  {
    pathname: BASE_DATA_EXPORTS_PATH,
    label: 'Data Exports',
    authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE, UserRole.GENETIC_COUNSELOR] },
    element: <ClinicalDataExports />,
  },
];

export const CLINICAL_METRICS_ROUTES: IRouteDetail[] = [
  {
    pathname: 'https://metabase.corp.color.com',
    label: 'Metabase',
  },
  {
    pathname: '/dashboard',
    label: 'Pipeline',
  },
  {
    pathname: BASE_SAMPLE_SEARCH_PATH,
    label: 'Sample Search',
    element: <SampleSearchView />,
  },
  {
    pathname: QC_METRICS_BATCHES_PATH,
    label: 'QC',
    authorization: {
      allowedRoles: [UserRole.COLOR_EMPLOYEE, UserRole.SAMPLE_REVIEWER],
    },
    element: <QCMetricsBatchListView />,
  },
].map((routeDetails) => ({
  ...routeDetails,
  authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE] },
}));

export const CLINICAL_SAMPLES_ROUTES: IRouteDetail[] = [
  {
    label: 'On Hold',
    pathname: `/samples/on_hold`,
    authorization: { requiredPermissions: [UserPermission.PUT_SAMPLES_ON_HOLD] },
    // Will use SamplesOnHold when React navbar is ready (UV-2385)
    // Until then, redirect to Angular version
    // element: <SamplesOnHold />,
  },
];

export const CLINICAL_ORDERS_ROUTES: IRouteDetail[] = [
  {
    label: 'Pending Physician-Ordering',
    authorization: { allowedRoles: [UserRole.ORDERING_PHYSICIAN] },
    pathname: '/orders/pending_physician_ordering',
  },
];

export const CLINICAL_PROVIDERS_ROUTES: IRouteDetail[] = [
  {
    pathname: '/prefill_provider',
    label: '1 Click Provider Account',
  },
  {
    pathname: '/providers/home',
    label: 'Physician Orders',
  },
  {
    pathname: '/physician_verifications/new',
    label: 'Physician Verification',
  },
].map((routeDetails) => ({
  ...routeDetails,
  authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE] },
}));

export const CLINICAL_RESULTS_ROUTES: IRouteDetail[] = [
  {
    pathname: '/reports/new/list',
    label: 'Reports',
    authorization: { allowedRoles: [UserRole.SAMPLE_REVIEWER] },
  },
  {
    pathname: '/covid_reports/list',
    label: 'Covid Reports',
    authorization: { requiredPermissions: [UserPermission.VIEW_COVID_REPORTS_QUEUE] },
  },
  {
    pathname: '/counseling/results/upcoming',
    label: 'Counseling',
    authorization: { allowedRoles: [UserRole.GENETIC_COUNSELOR] },
  },
];

export const CLINICAL_SUPPORT_ROUTES: IRouteDetail[] = [
  {
    pathname: '/support/cancel',
    label: 'Cancellation/Refund Tool',
    authorization: { allowedRoles: [UserRole.SUPPORT_ADMIN] },
  },
  {
    pathname: '/cst/support',
    label: 'Color Support Tools',
    authorization: { allowedRoles: [UserRole.SUPPORT_STAFF] },
  },
  {
    pathname: '/fax',
    label: 'Fax',
    authorization: { allowedRoles: [UserRole.SUPPORT_STAFF] },
  },
  {
    pathname: GENERIC_HEALTH_HISTORY_FIND_PATH,
    label: 'Health History',
    element: <HealthHistoryPage />,
    authorization: { requiredPermissions: [UserPermission.VIEW_HEALTH_HISTORY] },
  },
  {
    label: 'Partnership',
    pathname: '/partnership',
    authorization: {
      allowedRoles: [UserRole.SUPPORT_STAFF],
    },
  },
  {
    pathname: '/support/referrals',
    label: 'User Referral Codes',
    authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE] },
  },
  {
    pathname: '/support/schedulizer',
    label: 'GC Appointments',
    authorization: {
      requiredPermissions: [UserPermission.VIEW_USERS, UserPermission.VIEW_APPOINTMENTS],
    },
  },
  {
    pathname: '/support/coversheets',
    label: 'Coversheet Generator',
    authorization: { allowedRoles: [UserRole.SUPPORT_STAFF] },
  },
  {
    pathname: MARKETING_LINK_GENERATOR_PATH,
    label: 'Marketing Link Generator',
    authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE] },
  },
  {
    pathname: MARKETING_LINK_VALIDATOR_PATH,
    label: 'Marketing Link Validator',
    authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE] },
  },
];

export const CLINICAL_FULFILLMENT_ROUTES: IRouteDetail[] = [
  {
    pathname: `${BASE_FULFILLMENT_PATH}/international`,
    label: 'International Shipping',
  },
  {
    pathname: `${BASE_FULFILLMENT_PATH}/provision-manual-kio`,
    label: 'Provision KIO Kits',
  },
  {
    pathname: '/support/import-bulk-fulfillment-orders',
    label: 'Import Bulk Fulfillment Orders',
  },
  {
    pathname: '/cst/fulfillment',
    label: 'Tools',
  },
].map((routeDetails) => ({
  ...routeDetails,
  authorization: { allowedRoles: [UserRole.SUPPORT_STAFF] },
}));

export const CLINICAL_VARIANTS_ROUTES: IRouteDetail[] = [
  {
    pathname: IGV_PATH,
    label: 'IGV',
    authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE] },
    element: <IGVView />,
  },
  {
    pathname: BASE_CNV_CANDIDATES_PATH,
    label: 'CNV Review',
    authorization: { allowedRoles: [UserRole.COLOR_EMPLOYEE, UserRole.VARIANT_REVIEWER] },
    element: <CNVCandidatesBrowseReviewView />,
  },
  {
    pathname: `${VARIANT_CLASSIFICATION_LIST_PATH}?readyForReview=external&state=reviewed&clinicalDomain=cancer`,
    label: 'External Classification Review',
    authorization: {
      allowedRoles: [UserRole.EXTERNAL_CLASSIFICATION_REVIEWER],
    },
    element: <VariantClassificationListView />,
  },
  {
    pathname: `${VARIANT_CLASSIFICATION_LIST_PATH}?readyForReview=internal&state=reviewed`,
    label: 'Internal Classification Review',
    authorization: {
      allowedRoles: [UserRole.VARIANT_REVIEWER_MANAGER, UserRole.SAMPLE_REVIEWER],
    },
    element: <VariantClassificationListView />,
  },
  {
    pathname: VARIANT_CLASSIFICATION_LIST_PATH,
    label: 'Variant Classification',
    authorization: {
      allowedRoles: [
        UserRole.COLOR_EMPLOYEE,
        UserRole.VARIANT_REVIEWER,
        UserRole.EXTERNAL_CLASSIFICATION_REVIEWER,
      ],
    },
    element: <VariantClassificationListView />,
  },
  {
    pathname: VARIANT_CONFIRMATION_LIST_PATH,
    label: 'Confirmation',
    authorization: {
      allowedRoles: [UserRole.COLOR_EMPLOYEE, UserRole.LAB_STAFF],
    },
    element: <VariantConfirmationListView />,
  },
];

export const CLINICAL_SEARCH_ROUTE: IRouteDetail = {
  label: 'Search',
  authorization: { allowedRoles: [UserRole.SUPPORT_STAFF] },
  pathname: '/support/search',
};

const CLINICAL_ROUTE_DETAILS: IRouteDetail[] = [
  ...USER_MENU_ROUTES,
  ...CLINICAL_PARTNERS_ROUTES,
  ...CLINICAL_METRICS_ROUTES,
  ...CLINICAL_SAMPLES_ROUTES,
  ...CLINICAL_ORDERS_ROUTES,
  ...CLINICAL_PROVIDERS_ROUTES,
  ...CLINICAL_RESULTS_ROUTES,
  ...CLINICAL_SUPPORT_ROUTES,
  ...CLINICAL_FULFILLMENT_ROUTES,
  ...CLINICAL_VARIANTS_ROUTES,
  CLINICAL_SEARCH_ROUTE,
];

export const CLINICAL_REACT_ROUTE_DETAILS = CLINICAL_ROUTE_DETAILS.filter(({ element }) =>
  Boolean(element)
);

const CLINICAL_REACT_PATHS = CLINICAL_REACT_ROUTE_DETAILS.map(
  ({ pathname }: IRouteDetail): string => pathname
);

const CLINICAL_ANGULAR_ROUTE_DETAILS = CLINICAL_ROUTE_DETAILS.filter(({ element, pathname }) => {
  return !element && !pathname.startsWith(CLINICAL_BASE_PATH);
});

export const CLINICAL_ANGULAR_PATHS: string[] = compact(
  CLINICAL_ANGULAR_ROUTE_DETAILS.map(({ pathname }) => pathname)
);

// These are all of the paths that we currently handle with our ClinicalPortal
// Right now, the CLINICAL_BASE_PATH handles all React paths
// NOTE: Moving forward, we want to try to specify the path to a React page without prefixing it with
// the CLINICAL_BASE_PATH (/clinical/v2). In time we will move away from the prefix. It stays for now
// since some pages are already using it.
export const CLINICAL_PATHS =
  CLINICAL_ANGULAR_PATHS.concat(CLINICAL_BASE_PATH).concat(CLINICAL_REACT_PATHS);
